/**
 * Server-side analytics tracking utility
 */

export type EventCategory =
  | 'page_view'
  | 'bikepark_view'
  | 'map_interaction'
  | 'search'
  | 'filter'
  | 'contact'
  | 'navigation'
  | 'engagement';

export interface AnalyticsEvent {
  eventName: string;
  eventCategory: EventCategory;
  userId?: string;
  sessionId?: string;
  bikeparkSlug?: string;
  bikeparkId?: number;
  searchQuery?: string;
  filterValues?: Record<string, any>;
  mapZoom?: number;
  mapCenter?: { lat: number; lng: number };
  pageUrl?: string;
  locale?: string;
  metadata?: Record<string, any>;
}

/**
 * Track an analytics event
 * DISABLED: Internal analytics disabled to prevent database growth
 */
export async function trackEvent(event: AnalyticsEvent): Promise<void> {
  // Internal analytics disabled
  return;

  // Commented out to disable tracking
  // try {
  //   // Don't track in development unless explicitly enabled
  //   if (process.env.NODE_ENV === 'development' && !process.env.NEXT_PUBLIC_ENABLE_ANALYTICS) {
  //     console.log('[Analytics - Dev]', event.eventName, event);
  //     return;
  //   }

  //   await fetch('/api/analytics', {
  //     method: 'POST',
  //     headers: {
  //       'Content-Type': 'application/json',
  //     },
  //     body: JSON.stringify(event),
  //   });
  // } catch (error) {
  //   console.error('Failed to track event:', error);
  // }
}

/**
 * Generate a session ID for tracking (Client-side only)
 * NOTE: Not used in server-side tracking - cookies should be used instead
 */
// export function getSessionId(): string {
//   if (typeof window === 'undefined') return '';
//
//   let sessionId = sessionStorage.getItem('analytics_session_id');
//   if (!sessionId) {
//     sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
//     sessionStorage.setItem('analytics_session_id', sessionId);
//   }
//   return sessionId;
// }

/**
 * Generate a user ID for tracking (Client-side only)
 * NOTE: Not used in server-side tracking - cookies should be used instead
 */
// export function getUserId(): string {
//   if (typeof window === 'undefined') return '';
//
//   let userId = localStorage.getItem('analytics_user_id');
//   if (!userId) {
//     userId = `user_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
//     localStorage.setItem('analytics_user_id', userId);
//   }
//   return userId;
// }

// Pre-defined tracking functions for common events

export function trackPageView(pageUrl: string, locale?: string) {
  trackEvent({
    eventName: 'page_view',
    eventCategory: 'page_view',
    pageUrl,
    locale,
  });
}

export function trackBikeparkView(bikeparkSlug: string, bikeparkId?: number, locale?: string) {
  trackEvent({
    eventName: 'bikepark_view',
    eventCategory: 'bikepark_view',
    bikeparkSlug,
    bikeparkId,
    locale,
  });
}

export function trackSearch(searchQuery: string, locale?: string) {
  trackEvent({
    eventName: 'search',
    eventCategory: 'search',
    searchQuery,
    locale,
  });
}

export function trackMapInteraction(
  action: 'zoom' | 'pan' | 'marker_click',
  mapZoom?: number,
  mapCenter?: { lat: number; lng: number },
  bikeparkSlug?: string
) {
  trackEvent({
    eventName: `map_${action}`,
    eventCategory: 'map_interaction',
    mapZoom,
    mapCenter,
    bikeparkSlug,
  });
}

export function trackFilter(filterValues: Record<string, any>, locale?: string) {
  trackEvent({
    eventName: 'filter_applied',
    eventCategory: 'filter',
    filterValues,
    locale,
  });
}

export function trackContactFormSubmit(submissionType: string, locale?: string) {
  trackEvent({
    eventName: 'contact_form_submit',
    eventCategory: 'contact',
    metadata: { submissionType },
    locale,
  });
}

export function trackNavigation(destination: string, source?: string) {
  trackEvent({
    eventName: 'navigation',
    eventCategory: 'navigation',
    metadata: { destination, source },
  });
}
